import React, {useMemo} from "react";
import {Stepper, useSteps} from "@chakra-ui/react";
import {
  CREATE_NEW_PROJECT_SUBTITLES_STEPS,
  CREATE_PROJECT_ADDITIONAL_FEATURES_MAPPING,
  LANGUAGE_DEFAULT_CONFIGURATION,
} from "../../models/constants/constants.ts";
import StepperStep from "../StepperStep.tsx";
import CreateProjectModalFooter from "./CreateProjectModalFooter.tsx";
import {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import StepSourceLanguage from "./StepSourceLanguage.tsx";
import StepTargetLanguages from "./StepTargetLanguages.tsx";
import {ISubtitleState} from "../../reducers/createNewProjectReducer.ts";
import {buildLanguageConfigFromUserPreferences, getLanguageDefaultConfig} from "../../utils/utils.ts";
import useUserStore from "../../store/useUserStore.ts";
import {ILanguageCustomConfiguration} from "../../models/interfaces/languageConfiguration.ts";
import iso6391 from "iso-639-1";
import {IStepperStep} from "../../models/interfaces/steperItem.interface.ts";
import StepFeatures from "./StepFeatures.tsx";
import useSystemStore from "../../store/useSystemStore.ts";

interface SubtitlesStepProps {
  remainingMinutes?: number;
  totalDurationInMinutes: number;
  goToNext: () => void;
  goToPrevious: () => void;
  subtitlesState: ISubtitleState;
  setSourceLanguage: (payload: Record<string, ILanguageConfiguration>) => void;
  toggleIsTranscribeOnly: () => void;
  setTargetLanguage: (payload: Record<string, ILanguageConfiguration>) => void;
  toggleFeature: (feature: string) => void;
}

const SubtitlesStep: React.FC<SubtitlesStepProps> = (
  {
    remainingMinutes,
    totalDurationInMinutes,
    goToNext,
    goToPrevious,
    subtitlesState,
    setSourceLanguage,
    toggleIsTranscribeOnly,
    setTargetLanguage,
    toggleFeature
  }: SubtitlesStepProps) => {
  const {supportedCodeLanguages} = useSystemStore()
  const {languageConfig} = useUserStore()
  const subtitleFilteredSteps: IStepperStep[] = subtitlesState.isTranscribeOnly ? CREATE_NEW_PROJECT_SUBTITLES_STEPS.filter((stepperStep: IStepperStep) => stepperStep.title !== "Target Languages") : CREATE_NEW_PROJECT_SUBTITLES_STEPS;
  const {activeStep: localStep, goToNext: nextPhase, goToPrevious: prevPhase} = useSteps({
    index: 0,
    count: subtitleFilteredSteps.length,
  })
  // Derived booleans for clearer conditional rendering
  const showSourceStep: boolean = localStep === 0;
  const showTargetStep: boolean = localStep === 1 && !subtitlesState.isTranscribeOnly;
  const showFeaturesStep: boolean | undefined =
    (localStep === 2 && !subtitlesState.isTranscribeOnly) ||
    (localStep === 1 && subtitlesState.isTranscribeOnly);

  const supportedSourceLanguagesNames: string[] = useMemo(() => {
    return supportedCodeLanguages.map((langCode: string) => iso6391.getName(langCode));
  }, [supportedCodeLanguages]);

  const handleSelectSourceLanguage = (
    lang: string,
    configUpdate?: Partial<ILanguageConfiguration>
  ) => {
    setSourceLanguage(
      {
        [lang]: {
          ...subtitlesState.sourceLanguage[lang],
          ...configUpdate || getLanguageDefaultConfig(languageConfig, lang)
        }
      }
    )
  }

  const handleAddTargetLanguage = (lang: string): void => {
    const customConfig: ILanguageCustomConfiguration | undefined = languageConfig[iso6391.getCode(lang)]
    if (customConfig) {
      setTargetLanguage({
        ...subtitlesState.targetLanguages,
        [lang]: buildLanguageConfigFromUserPreferences(customConfig)
      })
    }

    setTargetLanguage({
      ...subtitlesState.targetLanguages,
      [lang]: LANGUAGE_DEFAULT_CONFIGURATION
    })
  };

  const handleRemoveTargetLanguage = (lang: string): void => {
    const newConfig: Record<string, ILanguageConfiguration> = {...subtitlesState.targetLanguages};
    delete newConfig[lang];
    setTargetLanguage(newConfig);
  };

  const handleUpdateTargetConfig = (lang: string, update: Partial<ILanguageConfiguration>): void => {
    setTargetLanguage({
      ...subtitlesState.targetLanguages,
      [lang]: {
        ...subtitlesState.targetLanguages[lang],
        ...update
      }
    })
  };

  const canContinueToNextStep = (): boolean => {
    if (localStep === 0) {
      return Object.keys(subtitlesState.sourceLanguage).length === 1;
    }

    if (localStep === 1) {
      if (subtitlesState.isTranscribeOnly) return true;
      else {
        const numberOfTargets: number = Object.keys(subtitlesState.targetLanguages).length
        const projectTotalDurationInMinutes: number = totalDurationInMinutes * numberOfTargets;
        const hasMinutesError: boolean = projectTotalDurationInMinutes > (remainingMinutes || 0);
        return Object.keys(subtitlesState.targetLanguages).length >= 1 && !hasMinutesError;
      }
    }

    return true;
  };

  const canProceed: boolean = canContinueToNextStep();


  return (
    <div className="flex flex-col">
      <div className="flex">
        <Stepper index={localStep} orientation='vertical'>
          {subtitleFilteredSteps.map((step: IStepperStep, index: number) => (
            <StepperStep key={index} step={step} isVertical isCompleted={index < localStep}/>
          ))}
        </Stepper>
        <div className="flex-1 ml-6">
          {showSourceStep && (
            <StepSourceLanguage
              selectedSourceLang={Object.keys(subtitlesState.sourceLanguage)[0] || ""}
              config={subtitlesState.sourceLanguage[Object.keys(subtitlesState.sourceLanguage)[0]] || LANGUAGE_DEFAULT_CONFIGURATION}
              onSelect={handleSelectSourceLanguage}
              onUpdateConfig={(partial: Partial<ILanguageConfiguration>) => {
                const lang: string = Object.keys(subtitlesState.sourceLanguage)[0];
                if (lang) handleSelectSourceLanguage(lang, partial);
              }}
              sourceLanguages={supportedSourceLanguagesNames}
              transcribeOnly={subtitlesState.isTranscribeOnly}
              toggleIsTranscribeOnly={toggleIsTranscribeOnly}
            />
          )}
          {showTargetStep && (
            <StepTargetLanguages
              availableLanguages={supportedSourceLanguagesNames}
              selectedSourceLanguage={Object.keys(subtitlesState.sourceLanguage)[0]}
              selectedTargets={subtitlesState.targetLanguages}
              onAddLanguage={handleAddTargetLanguage}
              onRemoveLanguage={handleRemoveTargetLanguage}
              setTargetConfig={handleUpdateTargetConfig}
            />
          )}
          {showFeaturesStep && (
            <StepFeatures
              features={CREATE_PROJECT_ADDITIONAL_FEATURES_MAPPING}
              selectedFeatures={subtitlesState.features || {} as Record<string, boolean>}
              onToggleFeature={toggleFeature}
            />
          )}
        </div>
      </div>
      <div className="flex justify-end mt-6 gap-4">
        {localStep === 0 && (
          <button
            key={'back-button'}
            className={`py-2 px-4 rounded-lg font-semibold bg-brandPurple-500 text-white`}
            onClick={() => goToPrevious()}
          >
            Back
          </button>
        )}
        <CreateProjectModalFooter
          activeStep={localStep}
          goToNext={nextPhase}
          goToPrevious={prevPhase}
          finalStep={subtitlesState.isTranscribeOnly ? CREATE_NEW_PROJECT_SUBTITLES_STEPS.length - 2 : CREATE_NEW_PROJECT_SUBTITLES_STEPS.length - 1}
          finalStepTitle={"Next"}
          canContinueToNextStep={canProceed}
          isFinalStepDisabled={false}
          onFinalStepClicked={goToNext}
        />
      </div>
    </div>
  )
}

export default SubtitlesStep;